<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-2xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-white font-display">Редактирование профиля</h1>
        <p class="mt-2 text-gray-400">Обновите информацию о вашем аккаунте</p>
      </div>

      <!-- Profile Form Card -->
      <div class="card">
        <form @submit.prevent="handleUpdate" class="space-y-6 p-6">
          <!-- Name Field -->
          <div>
            <label class="form-label">Имя</label>
            <input
              v-model="form.name"
              type="text"
              required
              class="input-field"
              placeholder="Введите ваше имя"
            />
            <div v-if="errors.name" class="mt-1 text-sm text-red-400">
              {{ errors.name }}
            </div>
          </div>

          <!-- Email Field -->
          <div>
            <label class="form-label">Email</label>
            <input
              v-model="form.email"
              type="email"
              required
              class="input-field"
              placeholder="Введите ваш email"
            />
            <div v-if="errors.email" class="mt-1 text-sm text-red-400">
              {{ errors.email }}
            </div>
          </div>

          <!-- Password Section -->
          <div class="border-t border-gray-800 pt-6">
            <h3 class="text-lg font-semibold text-white mb-4 font-display">
              Изменить пароль
            </h3>
            <p class="text-sm text-gray-400 mb-4">
              Оставьте поля пустыми, если не хотите менять пароль
            </p>

            <!-- Current Password -->
            <div class="mb-4">
              <label class="form-label">Текущий пароль</label>
              <div class="relative">
                <input
                  v-model="form.current_password"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  class="input-field pr-10"
                  placeholder="Введите текущий пароль"
                />
                <button
                  type="button"
                  @click="showCurrentPassword = !showCurrentPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
                >
                  <svg
                    v-if="showCurrentPassword"
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <div v-if="errors.current_password" class="mt-1 text-sm text-red-400">
                {{ errors.current_password }}
              </div>
            </div>

            <!-- New Password -->
            <div class="mb-4">
              <label class="form-label">Новый пароль</label>
              <div class="relative">
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="input-field pr-10"
                  placeholder="Введите новый пароль"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
                >
                  <svg
                    v-if="showPassword"
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <div v-if="errors.password" class="mt-1 text-sm text-red-400">
                {{ errors.password }}
              </div>
            </div>

            <!-- Confirm Password -->
            <div>
              <label class="form-label">Подтвердите новый пароль</label>
              <div class="relative">
                <input
                  v-model="form.password_confirmation"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  class="input-field pr-10"
                  placeholder="Подтвердите новый пароль"
                />
                <button
                  type="button"
                  @click="showPasswordConfirm = !showPasswordConfirm"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
                >
                  <svg
                    v-if="showPasswordConfirm"
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <div v-if="errors.password_confirmation" class="mt-1 text-sm text-red-400">
                {{ errors.password_confirmation }}
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-4 pt-6 border-t border-gray-800">
            <button
              type="button"
              @click="$router.push('/dashboard')"
              class="btn-secondary px-6 py-2 text-sm font-medium"
            >
              Отмена
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="btn-primary px-6 py-2 text-sm font-medium flex items-center"
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ loading ? 'Сохранение...' : 'Сохранить изменения' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../stores/auth';
import { useNotifications } from '../composables/useNotifications';

export default {
  name: 'Profile',
  setup() {
    const authStore = useAuthStore();
    const { success, error } = useNotifications();

    return {
      authStore,
      success,
      error,
    };
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        current_password: '',
        password: '',
        password_confirmation: '',
      },
      showCurrentPassword: false,
      showPassword: false,
      showPasswordConfirm: false,
      errors: {},
      loading: false,
    };
  },
  mounted() {
    this.loadUserData();
  },
  methods: {
    loadUserData() {
      const user = this.authStore.currentUser;
      if (user) {
        this.form.name = user.name || '';
        this.form.email = user.email || '';
      } else {
        this.authStore.fetchUser().then(() => {
          const user = this.authStore.currentUser;
          if (user) {
            this.form.name = user.name || '';
            this.form.email = user.email || '';
          }
        });
      }
    },
    validateForm() {
      this.errors = {};

      if (!this.form.name) {
        this.errors.name = 'Введите имя';
        return false;
      }

      if (this.form.name.length < 2) {
        this.errors.name = 'Имя должно содержать минимум 2 символа';
        return false;
      }

      if (!this.form.email) {
        this.errors.email = 'Введите email';
        return false;
      }

      if (!this.isValidEmail(this.form.email)) {
        this.errors.email = 'Введите корректный email';
        return false;
      }

      if (this.form.password || this.form.password_confirmation || this.form.current_password) {
        if (!this.form.current_password) {
          this.errors.current_password = 'Введите текущий пароль для изменения';
          return false;
        }

        if (!this.form.password) {
          this.errors.password = 'Введите новый пароль';
          return false;
        }

        if (this.form.password.length < 8) {
          this.errors.password = 'Пароль должен содержать минимум 8 символов';
          return false;
        }

        if (!this.isValidPassword(this.form.password)) {
          this.errors.password =
            'Пароль должен содержать минимум одну строчную букву, одну заглавную букву и одну цифру';
          return false;
        }

        if (!this.form.password_confirmation) {
          this.errors.password_confirmation = 'Подтвердите новый пароль';
          return false;
        }

        if (this.form.password !== this.form.password_confirmation) {
          this.errors.password_confirmation = 'Пароли не совпадают';
          return false;
        }
      }

      return true;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    isValidPassword(password) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
      return passwordRegex.test(password);
    },

    async handleUpdate() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.loading = true;

        const updateData = {
          name: this.form.name,
          email: this.form.email,
        };

        if (this.form.password) {
          updateData.password = this.form.password;
          updateData.password_confirmation = this.form.password_confirmation;
        }

        await this.authStore.updateProfile(updateData);

        this.success('Успех', 'Профиль успешно обновлен');

        this.form.current_password = '';
        this.form.password = '';
        this.form.password_confirmation = '';

        setTimeout(() => {
          this.$router.push('/dashboard');
        }, 1500);
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Ошибка обновления профиля';
        const errorDetails = err.response?.data?.errors;

        if (errorDetails) {
          this.errors = { ...this.errors, ...errorDetails };
          this.error('Ошибка валидации', errorMessage);
        } else {
          this.error('Ошибка', errorMessage);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
